import { api } from "@/lib/api-client"

// =============================================================================
// Document Intelligence Quota Management
// =============================================================================

export type DocintQuotaPeriod = {
  spend_sek: number
  limit_sek: number
  remaining_sek: number
  is_blocked: boolean
}

export type DocintPoolStats = {
  spend_sek: number
  page_count: number
  call_count: number
  monthly_limit_sek: number
  daily_limit_sek: number
  monthly_remaining_sek: number
  is_blocked: boolean
}

export type DocintBlockedEntity = {
  scope: string
  type: "global" | "pool" | "user"
  pool?: string
  user_id?: string
  period: "daily" | "monthly"
  current_spend_sek: number
  limit_sek: number
}

export type DocintWarningEntity = {
  scope: string
  type: "global" | "pool" | "user"
  pool?: string
  user_id?: string
  period: "daily" | "monthly"
  current_spend_sek: number
  limit_sek: number
  percentage: number
}

export type DocintTopUser = {
  user_id: string
  spend_sek: number
  page_count: number
  call_count: number
  daily_spend_sek: number
  monthly_limit_sek: number
  daily_limit_sek: number
  monthly_percentage: number
  daily_percentage: number
  status: "blocked" | "warning" | "ok"
  status_period: "daily" | "monthly" | null
  profile?: {
    name?: string | null
    email?: string | null
    jobTitle?: string | null
    department?: string | null
    company?: string | null
  }
}

export type DocintOverride = {
  scope_type: "global" | "pool" | "user"
  scope_id?: string
  period: "daily" | "monthly"
  override_type: "limit" | "extra_budget" | "bypass"
  value_sek: number | null
  reason: string
  granted_by: string
  granted_at: string
  expires_at?: string
  // Revocation fields (for audit trail)
  revoked_at?: string
  revoked_by?: string
  revoked_reason?: string
}

export type DocintDashboardResponse = {
  year_month: string
  date: string
  global: {
    spend_sek: number
    page_count: number
    call_count: number
    daily_spend_sek?: number
    monthly: DocintQuotaPeriod
    daily: DocintQuotaPeriod
  }
  pools: {
    user: DocintPoolStats
    indexing: DocintPoolStats
  }
  top_users: DocintTopUser[]
  active_overrides: DocintOverride[]
  blocked_entities: DocintBlockedEntity[]
  warning_entities: DocintWarningEntity[]
  config: {
    limits: Record<string, number>
    active_overrides: number
  }
}

export type DocintUserStatusResponse = {
  user_id: string
  year_month: string
  date: string
  quota_status: {
    scope: string
    monthly: DocintQuotaPeriod
    daily: DocintQuotaPeriod
    is_exceeded: boolean
    page_count: number
    call_count: number
    has_override: boolean
    override_reason?: string
  } | null
  stats: {
    spend_sek: number
    page_count: number
    call_count: number
  }
  has_override: boolean
  override: DocintOverride | null
  all_scopes: Array<{
    scope: string
    monthly: DocintQuotaPeriod
    daily: DocintQuotaPeriod
    is_exceeded: boolean
    page_count: number
    call_count: number
    has_override: boolean
    override_reason?: string
  }>
}

export type DocintConfigResponse = {
  limits: Record<string, number>
  overrides: Record<string, DocintOverride>
  warning_threshold_percent: number
}

export type DocintConfigUpdate = {
  limits?: Record<string, number>
  warning_threshold_percent?: number
}

export type DocintOverrideCreate = {
  scope_type: "global" | "pool" | "user"
  scope_id?: string
  period: "daily" | "monthly"
  override_type: "limit" | "extra_budget" | "bypass"
  value_sek?: number
  reason: string
  expires_at?: string
}

export type DocintUnlockRequest = {
  scope_type: "global" | "pool" | "user"
  scope_id?: string
  reason?: string
  period?: "daily" | "monthly" | "both"
}

export async function getDocintDashboard(
  yearMonth?: string,
  date?: string
): Promise<DocintDashboardResponse> {
  const params = new URLSearchParams()
  if (yearMonth) params.append("year_month", yearMonth)
  if (date) params.append("date", date)
  const query = params.toString() ? `?${params.toString()}` : ""
  const response = await api.get(`/admin/docint/dashboard${query}`)
  return response.data || response
}

export async function getDocintUserStatus(
  userId: string,
  yearMonth?: string,
  date?: string
): Promise<DocintUserStatusResponse> {
  const params = new URLSearchParams()
  if (yearMonth) params.append("year_month", yearMonth)
  if (date) params.append("date", date)
  const query = params.toString() ? `?${params.toString()}` : ""
  const response = await api.get(`/admin/docint/user/${userId}${query}`)
  return response.data || response
}

export async function getDocintConfig(): Promise<DocintConfigResponse> {
  const response = await api.get("/admin/docint/config")
  return response.data || response
}

export async function updateDocintConfig(
  config: DocintConfigUpdate
): Promise<{ success: boolean; config: DocintConfigResponse }> {
  const response = await api.put("/admin/docint/config", config)
  return response.data || response
}

export async function createDocintOverride(
  override: DocintOverrideCreate
): Promise<{ success: boolean; override: DocintOverride }> {
  const response = await api.post("/admin/docint/overrides", override)
  return response.data || response
}

export async function revokeDocintOverride(
  key: string,
  reason?: string
): Promise<{ success: boolean; message: string; override: DocintOverride }> {
  const response = await api.post(`/admin/docint/overrides/${encodeURIComponent(key)}/revoke`, {
    reason: reason || "Revoked by admin",
  })
  return response.data || response
}

export async function unlockDocintScope(
  unlock: DocintUnlockRequest
): Promise<{ success: boolean; override: DocintOverride }> {
  const response = await api.post("/admin/docint/unlock", unlock)
  return response.data || response
}

export async function refreshDocintConfig(): Promise<{ success: boolean; message: string }> {
  const response = await api.post("/admin/docint/refresh")
  return response.data || response
}
